import { useCallback } from 'react';

import { useItemParam } from '../../hooks/useItemParam.js';
import ItemUnavailableSlideOut from './ItemUnavailableSlideOut.jsx';
import { ITEM_SLIDEOUT_COMPONENTS } from './itemSlideOutRegistry.js';

/**
 * The ONE slide-out of a profile (CT-5715), rendered once by the profile page.
 *
 * The open item lives in the URL as `?item=<kind>:<id>`, as in the prototype, so
 * any tab — and the How to approach rows (CT-5714) — open an item with the same
 * link, a pasted URL reopens it, and Back closes it. Closing removes the param.
 *
 * The component is picked from the registry by kind; an unknown, reserved or
 * malformed link opens the unavailable state rather than nothing, so a stale
 * link explains itself. Keyed by the param so switching item remounts clean.
 *
 * "Open in History" from an item (CT-5716) goes to the profile's History tab
 * with `?event=<id>` — leaving `?item` behind, so the slide-out closes.
 *
 * `onItemChanged` tells the page an item was changed from its slide-out (a
 * report), so the How to approach card that reads from it refreshes (CT-5714).
 */
function ProfileItemSlideOut({ entityType, entityId, entityTitle, historyTabId, openTab, onItemChanged }) {
  const { raw, item, closeItem } = useItemParam();

  const openHistoryEvent = useCallback(
    (eventId) => openTab(historyTabId, `?event=${encodeURIComponent(eventId)}`),
    [openTab, historyTabId],
  );

  if (!raw) return null;

  const Component = item ? ITEM_SLIDEOUT_COMPONENTS[item.kind] : undefined;
  if (!Component) return <ItemUnavailableSlideOut onClose={closeItem} />;

  return (
    <Component
      key={raw}
      itemId={item.id}
      entityType={entityType}
      entityId={entityId}
      entityTitle={entityTitle}
      onClose={closeItem}
      onOpenHistoryEvent={historyTabId && openTab ? openHistoryEvent : undefined}
      onChanged={onItemChanged}
    />
  );
}

export default ProfileItemSlideOut;
